import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { forEach } from 'lodash'
import routes from '../routes/routes'
import storageService from './storage.service'
import alert from './alert.service'
import progressBar from './progressBar.service'
import { getLanguage } from '../i18n/i18n'

type Target = 'http' | 'backend'
type ContentType = 'json' | 'form'
type ContentMimes = Record<ContentType, string>

export interface ResponseCallback<ResponseData> {
    (data: ResponseData | null): void
}

export interface ResponseAndErrorCallback {
    (error: any | null): void
}

export interface PaginateRequest {
    page: number
    limit: number
    search?: string
}

export interface PaginateWithTypeRequest {
    page: number
    limit: number
    search?: string
    labelableType?: string
    sortBy?: string
}

export interface PaginateResponse<PaginateData> {
    total: number
    from: number | null
    to: number | null
    currentPage: number
    lastPage: number
    data: PaginateData
}

const contentMimes: ContentMimes = {
  json: 'application/json',
  form: 'multipart/form-data'
}

const api = {
  client (
    target: Target = 'backend',
    contentType: ContentType = 'json',
    poll: boolean = false
  ) {
    const token = storageService.get('authToken')
    const client = target === 'backend'
      ? axios.create({
        baseURL: `${process.env.REACT_APP_API_URL}`,
        headers: {
          Authorization: `Bearer ${token}`,
          ContentType: contentMimes[contentType],
          'X-App-Locale': getLanguage()
        }
      })
      : axios.create()
    const request = {
      success (config: AxiosRequestConfig) {
        progressBar.add()
        return config
      },
      error (error: any) {
        progressBar.remove()
        return Promise.reject(error)
      }
    }
    const response = {
      success (response: AxiosResponse) {
        progressBar.remove()
        return response
      },
      error (error: any) {
        progressBar.remove()
        return Promise.reject(error)
      }
    }
    if (!poll) {
      client.interceptors.request.use(request.success, request.error)
      client.interceptors.response.use(response.success, response.error)
    }
    return client
  },

  handleError (error: any) {
    const { response } = error
    const unauthorized = (response && response.status === 401)
    const requestError = (response && response.data.message)
    const message = requestError || error.message
    alert.error(message)
    if (unauthorized) {
      storageService.remove('authToken')
      window.location.href = routes.signIn
    }
    console.error(error)
  },

  get<ResponseData> (
    url: string,
    params: object,
    callback: ResponseCallback<ResponseData>,
    target: Target = 'backend',
    poll: boolean = false
  ) {
    this.client(target, 'json', poll).get<ResponseData>(url, { params })
      .then(response => callback(response.data))
      .catch(error => {
        callback(null)
        if (!poll) this.handleError(error)
      })
  },

  getAndError<ResponseData> (
    url: string,
    params: object,
    callback: ResponseCallback<ResponseData>,
    errorCallback: ResponseAndErrorCallback
  ) {
    this.client().get<ResponseData>(url, { params })
      .then(response => callback(response.data))
      .catch(error => errorCallback(error))
  },

  post<RequestData, ResponseData> (
    url: string,
    payload: RequestData,
    callback: ResponseCallback<ResponseData>
  ) {
    console.log('payload', payload)
    this.client().post<ResponseData>(url, payload)
      .then(response => {
          callback(response.data)
      })
      .catch(error => {
        callback(null)
        this.handleError(error)
      })
  },

  postAndError<RequestData, ResponseData> (
    url: string,
    payload: RequestData,
    callback: ResponseCallback<ResponseData>,
    errorCallback: ResponseAndErrorCallback
  ) {
    this.client().post<ResponseData>(url, payload)
      .then(response => callback(response.data))
      .catch(error => {
        errorCallback(error)
      })
  },

  postFormAndError<RequestData, ResponseData> (
    url: string,
    payload: RequestData,
    callback: ResponseCallback<ResponseData>,
    errorCallback: ResponseAndErrorCallback
  ) {
    const fields = payload as unknown as object
    const formData = new FormData()
    forEach(fields, (value, key) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value)
      }
    })

    this.client('backend', 'form')
      .post<ResponseData>(url, formData)
      .then(response => callback(response.data))
      .catch(error => errorCallback(error))
  },

  postForm<RequestData, ResponseData> (
    url: string,
    payload: RequestData,
    callback: ResponseCallback<ResponseData>
  ) {
    const fields = payload as unknown as object
    const formData = new FormData()
    forEach(fields, (value, key) => formData.append(key, value))
    this.client('backend', 'form')
      .post<ResponseData>(url, formData)
      .then(response => callback(response.data))
      .catch(error => {
        callback(null)
        this.handleError(error)
      })
  },

  patch<RequestData, ResponseData> (
    url: string,
    payload: RequestData,
    callback: ResponseCallback<ResponseData>
  ) {
    this.client().patch<ResponseData>(url, payload)
      .then(response => callback(response.data))
      .catch(error => {
        callback(null)
        this.handleError(error)
      })
  },

  delete<RequestData, ResponseData> (
    url: string,
    payload: RequestData,
    callback: ResponseCallback<ResponseData>
  ) {
    const config = { data: payload }
    this.client().delete<ResponseData>(url, config)
      .then(response => callback(response.data))
      .catch(error => {
        callback(null)
        this.handleError(error)
      })
  }
}

export default api
